import { Reservation } from "../types/bookingCreation";

export type Coupon = {
    code: string
    amount: number
  }

export const calculateTotalPrice = (reservations: Reservation[]): number => {
    return reservations.reduce((total, reservation) => {
      const reservationItemsTotalPrice = reservation.reservationItems.reduce((total, item) => {
        const price = item.price;
        return total + price
      }, 0);

      return total + reservationItemsTotalPrice
    }, 0);
  };

  export const findCoupons = (reservations: Reservation[]) => {
    const foundCoupons: Coupon[] = []

    for (const reservation of reservations) {
      const code = reservation.payment.voucher?.code;
      // const amount = parseFloat((reservation.reservationItems[0].price - (reservation.payment.discount_amount_applied || 0)).toFixed(2));
      const amount = reservation.payment.discount_amount_applied || 0


      if (code && amount) {
        foundCoupons.push({ code, amount });
      }
    }

    return foundCoupons
  }

  export function consolidateCoupons(coupons: Coupon[]): Coupon[] {
    const couponMap = new Map<string, number>();

    coupons.forEach(({ code, amount }) => {
      const currentAmount = parseFloat((couponMap.get(code) || 0).toFixed(2));
      const formattedAmount = parseFloat((currentAmount + parseFloat(amount.toFixed(2))).toFixed(2));

      couponMap.set(code, formattedAmount)
    }
    );

    return Array.from(couponMap, ([code, amount]) => ({ code, amount }));
  }


 export const calculateDisplayPrice = (totalPrice: number, coupons: Coupon[]) => {

    const totalDiscountAmount = coupons.reduce((total, coupon) => {
      return total + coupon.amount
    }, 0)

    if (totalPrice > totalDiscountAmount) {
      return parseFloat((totalPrice - totalDiscountAmount).toFixed(2))
    }

    return 0
}

export const calculateDisplayPriceForReservations = (reservations: Reservation[]): number => {
  const totalPrice = calculateTotalPrice(reservations);
  return calculateDisplayPrice(totalPrice, findCoupons(reservations));
}

/**
 * The amount the customer is actually asked to pay.
 *
 * Prefers the backend's `requested_amount` — it already accounts for an applied
 * coupon — and falls back to the item price when the backend did not send one.
 * Deliberately reads `reservationItems[0]` only: this mirrors the reservation
 * shape the payment step has always assumed (one item per reservation), and
 * this value decides whether the customer is routed to Stripe, so it must not
 * start counting differently than it does today.
 *
 * Not the same question as `calculateTotalPrice`, which is the gross list price
 * before coupons and is what the summary ticket prints.
 *
 * The value is coerced with `Number` because the p2p flow's own copy of this
 * calculation carried a `parseFloat` guard against a price arriving as a string,
 * and repointing that flow here must not quietly drop the guard. A price that
 * will not parse yields `NaN`, which `requiresPayment` reads as "requires
 * payment" — the safe direction, since the alternative is handing out a booking
 * for free on bad data.
 */
export const calculateRequestedAmount = (reservations: Reservation[]): number => {
  return reservations.reduce((total, reservation) => {
    const price = Number(
      reservation.payment.requested_amount ?? reservation.reservationItems[0].price
    );
    return total + price;
  }, 0);
};

/**
 * Single definition of "does this booking cost anything?".
 *
 * Owned here so the payment step's routing (Stripe vs. NoPayment) and any
 * screen wording that promises payment cannot drift apart.
 */
export const requiresPayment = (reservations: Reservation[]): boolean => {
  return Number(calculateRequestedAmount(reservations)) !== 0;
};

/**
 * Is this booking free of charge — i.e. is there a price, and is it zero?
 *
 * TWO questions, and the second one is why `pricedItemCount` is required rather
 * than optional. "The total is 0" does not mean free; it also means *nothing has
 * loaded yet*, and every screen here reaches zero that way:
 *
 *   - `calculateTotalPrice` reduces from `0`, so an empty reservation list —
 *     before the fetch resolves, or after the last one is deleted — returns 0.
 *   - `KeyHandoverTicket` reads `reservations[0]?.payment.totalPrice ?? 0`, so a
 *     missing reservation or an absent amount becomes 0 before this function can
 *     see that it was absent.
 *   - `KeyHandoverSubscriptionSummaryStep` reads `selectedPlan?.price || 0`, so
 *     "no plan chosen" becomes 0 the same way.
 *
 * Taking the price alone, this hid the promo-code field on a PAID booking for
 * as long as its price took to arrive — a control vanishing for a reason the
 * customer cannot see. So the caller must state how many priced things the
 * total covers, and a count of zero means **unknown, not free**.
 *
 * Pass the **list** price, before any voucher, and pass it RAW — do not default
 * it to 0 on the way in, or the guard below cannot do its job. A paid booking a
 * coupon reduces to zero is not free: it had a price, and the tax line still
 * belongs on it. (That case cannot reach the coupon field anyway — every
 * `showCouponButton` already requires `coupons.length === 0`.)
 *
 * ── SCOPE: this decides the TAX LINE and the COUPON FIELD, nothing else ──
 *
 * ADR-0008's amendment of 2026-09-14 deliberately kept the price rows — per
 * item, total, separator — visible on a free booking, because at the KGSt-Forum
 * expo the bookers are external prospects for whom a free locker is an anomaly,
 * so `0,00 €` states the concession instead of leaving them to guess. That
 * still holds, and the `!skipPayment` guards on those rows are untouched.
 *
 * The 2026-09-16 amendment narrows it: the concession argument justifies the
 * price rows, not "Alle Preise inkl. Steuern" — a legal statement about prices
 * that do not exist — nor a promo-code field that cannot reduce zero. Read both
 * amendments before wiring anything else to this.
 *
 * @param listPrice        the total as the screen computes it, before vouchers
 * @param pricedItemCount  how many priced things that total covers; 0 means
 *                         nothing is loaded or selected, which is not free
 */
export const isFreeOfCharge = (
  listPrice: number | string | null | undefined,
  pricedItemCount: number
): boolean => {
  // No priced item — the total is zero for lack of data, not for lack of price.
  if (!Number.isFinite(pricedItemCount) || pricedItemCount <= 0) return false;

  // `Number(null)` and `Number("")` are both `0`, so an absent amount would
  // otherwise read as free and drop a legal statement.
  if (listPrice === null || listPrice === undefined) return false;
  if (typeof listPrice === "string" && listPrice.trim() === "") return false;

  const amount = Number(listPrice);
  return Number.isFinite(amount) && amount <= 0;
};
